import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlineLocalShipping } from "react-icons/md";
import useMarketplaceShippingFees, {
  DEFAULT_SHIPPING_FEES,
} from "../../../Routes/Marketplace/libs/useMarketplaceShippingFees";
import useUpdateMarketplaceShippingFees from "../../../marketplace_admin/libs/useUpdateMarketplaceShippingFees";
import PageHeader from "../../components/PageHeader";
import Spinner from "../../../Spinner/Spinner";

const Field = ({ label, hint, children, full }) => (
  <label className={`dashboard-form-field${full ? " dashboard-form-field--full" : ""}`}>
    <span>{label}</span>
    {hint && <span className="dashboard-form-hint">{hint}</span>}
    {children}
  </label>
);

const AdminShippingPage = () => {
  const { data: settings, isLoading, isError } = useMarketplaceShippingFees();
  const updateMutation = useUpdateMarketplaceShippingFees();
  const [form, setForm] = useState({ ...DEFAULT_SHIPPING_FEES });

  useEffect(() => {
    if (settings) {
      setForm({
        ...DEFAULT_SHIPPING_FEES,
        ...settings,
        pickupFee: settings.pickupFee ?? DEFAULT_SHIPPING_FEES.pickupFee,
        doorFee: settings.doorFee ?? DEFAULT_SHIPPING_FEES.doorFee,
      });
    }
  }, [settings]);

  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const pickup = parseFloat(form.pickupFee);
    const door = parseFloat(form.doorFee);
    const podMax = parseFloat(form.payOnDeliveryMaxAmount);
    const podDays = parseInt(form.payOnDeliveryBlockDays, 10);

    if (Number.isNaN(pickup) || pickup < 0 || Number.isNaN(door) || door < 0) {
      toast.error("Enter valid non-negative amounts for both fees.");
      return;
    }

    if (form.payOnDeliveryEnabled !== false) {
      if (Number.isNaN(podMax) || podMax < 0) {
        toast.error("Enter a valid pay-on-delivery maximum amount.");
        return;
      }
      if (Number.isNaN(podDays) || podDays < 1) {
        toast.error("Block duration must be at least 1 day.");
        return;
      }
    }

    try {
      const result = await updateMutation.mutateAsync({
        ...form,
        pickupFee: pickup,
        doorFee: door,
        payOnDeliveryEnabled: form.payOnDeliveryEnabled !== false,
        payOnDeliveryMaxAmount: Number.isNaN(podMax) ? 200000 : podMax,
        payOnDeliveryBlockDays: Number.isNaN(podDays) ? 60 : podDays,
        payOnDeliveryAllowGuest: form.payOnDeliveryAllowGuest === true,
      });
      if (result?.settings) {
        setForm({ ...DEFAULT_SHIPPING_FEES, ...result.settings });
      }
      toast.success("Shipping settings saved.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update shipping settings.");
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title="Shipping" subtitle="Delivery fees and checkout shipping copy" icon={MdOutlineLocalShipping} />
        <Spinner />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <PageHeader title="Shipping" subtitle="Delivery fees and checkout shipping copy" icon={MdOutlineLocalShipping} />
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load shipping settings.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Shipping"
        subtitle="Manage delivery fees and all shipping text shown on product pages and at checkout"
        icon={MdOutlineLocalShipping}
        actions={
          <button
            type="submit"
            form="admin-shipping-form"
            className="dashboard-btn dashboard-btn--primary"
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? "Saving…" : "Save changes"}
          </button>
        }
      />

      <form id="admin-shipping-form" onSubmit={handleSubmit} className="admin-shipping-form">
        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Section heading</h2>
          <Field label="Title on product page" hint="Shown above pickup, door delivery, and return policy." full>
            <input type="text" value={form.sectionTitle} onChange={setField("sectionTitle")} required />
          </Field>
        </section>

        <div className="admin-shipping-form__grid">
          <section className="dashboard-card">
            <h2 className="dashboard-card__title">Pickup station</h2>
            <div className="dashboard-form-grid">
              <Field label="Display label"><input type="text" value={form.pickupLabel} onChange={setField("pickupLabel")} required /></Field>
              <Field label="Delivery fee (NGN)"><input type="number" min="0" step="1" value={form.pickupFee} onChange={setField("pickupFee")} required /></Field>
              <Field label="Estimate text" full><input type="text" value={form.pickupEstimate} onChange={setField("pickupEstimate")} required /></Field>
              <Field label="Details popup text" full><textarea rows={3} value={form.pickupDetailsText} onChange={setField("pickupDetailsText")} required /></Field>
            </div>
          </section>

          <section className="dashboard-card">
            <h2 className="dashboard-card__title">Door delivery</h2>
            <div className="dashboard-form-grid">
              <Field label="Display label"><input type="text" value={form.doorLabel} onChange={setField("doorLabel")} required /></Field>
              <Field label="Delivery fee (NGN)"><input type="number" min="0" step="1" value={form.doorFee} onChange={setField("doorFee")} required /></Field>
              <Field label="Estimate text" full><input type="text" value={form.doorEstimate} onChange={setField("doorEstimate")} required /></Field>
              <Field label="Details popup text" full><textarea rows={3} value={form.doorDetailsText} onChange={setField("doorDetailsText")} required /></Field>
            </div>
          </section>
        </div>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Return policy</h2>
          <div className="dashboard-form-grid">
            <Field label="Title"><input type="text" value={form.returnPolicyTitle} onChange={setField("returnPolicyTitle")} required /></Field>
            <Field label="Details link path" hint="Internal route, e.g. /market/return-policy"><input type="text" value={form.returnPolicyLink} onChange={setField("returnPolicyLink")} required /></Field>
            <Field label="Description" full><textarea rows={2} value={form.returnPolicyText} onChange={setField("returnPolicyText")} required /></Field>
          </div>
        </section>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Pay on delivery</h2>
          <p className="dashboard-form-hint">Control cash-on-delivery at checkout.</p>
          <label className="dashboard-form-field dashboard-form-field--full">
            <span className="dashboard-checkbox-row">
              <input
                type="checkbox"
                checked={form.payOnDeliveryEnabled !== false}
                onChange={(e) => setForm((prev) => ({ ...prev, payOnDeliveryEnabled: e.target.checked }))}
              />
              Enable pay on delivery
            </span>
          </label>
          <div className="dashboard-form-grid">
            <Field label="Maximum order amount (NGN)">
              <input type="number" min="0" step="1000" value={form.payOnDeliveryMaxAmount ?? 200000} onChange={setField("payOnDeliveryMaxAmount")} disabled={form.payOnDeliveryEnabled === false} />
            </Field>
            <Field label="Block duration (days)">
              <input type="number" min="1" step="1" value={form.payOnDeliveryBlockDays ?? 60} onChange={setField("payOnDeliveryBlockDays")} disabled={form.payOnDeliveryEnabled === false} />
            </Field>
          </div>
          <label className="dashboard-form-field dashboard-form-field--full">
            <span className="dashboard-checkbox-row">
              <input
                type="checkbox"
                checked={form.payOnDeliveryAllowGuest === true}
                onChange={(e) => setForm((prev) => ({ ...prev, payOnDeliveryAllowGuest: e.target.checked }))}
                disabled={form.payOnDeliveryEnabled === false}
              />
              Allow guest checkout with pay on delivery
            </span>
          </label>
        </section>

        <section className="dashboard-card">
          <h2 className="dashboard-card__title">Shared labels</h2>
          <div className="dashboard-form-grid">
            <Field label="Delivery fee label"><input type="text" value={form.deliveryFeeLabel} onChange={setField("deliveryFeeLabel")} required /></Field>
            <Field label="Details button text"><input type="text" value={form.detailsButtonLabel} onChange={setField("detailsButtonLabel")} required /></Field>
            <Field label="Popup title"><input type="text" value={form.overlayTitle} onChange={setField("overlayTitle")} required /></Field>
            <Field label="Popup fee label"><input type="text" value={form.overlayFeeLabel} onChange={setField("overlayFeeLabel")} required /></Field>
          </div>
        </section>

        {settings?.updatedAt && (
          <p style={{ color: "var(--dash-muted)", fontSize: 13, margin: 0 }}>
            Last updated: {new Date(settings.updatedAt).toLocaleString()}
          </p>
        )}
      </form>
    </>
  );
};

export default AdminShippingPage;
